import { computed, ref, watch, readonly, type Ref, type ComputedRef } from 'vue';
import { storageService } from '@/common/services/storage/storageService';

export type ThemeMode = 'modern' | 'classic' | 'dark' | 'system';

export interface ThemeColors {
  // Brand colors
  primary: string;
  secondary: string;
  tertiary: string;

  // Semantic colors
  success: string;
  warning: string;
  danger: string;
  info: string;

  // Background colors
  background: string;
  backgroundPrimary: string;
  backgroundSecondary: string;
  backgroundTertiary: string;
  backgroundQuaternary: string;

  // Text colors
  text: string;
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textDisabled: string;

  // Component colors
  toolbarBackground: string;
  toolbarColor: string;
  cardBackground: string;
  cardColor: string;
  itemBackground: string;
  itemColor: string;

  // Input colors
  inputBackground: string;
  inputColor: string;
  inputBorderColor: string;
  inputBorderColorFocus: string;
  inputPlaceholderColor: string;

  // Button colors
  buttonBackground: string;
  buttonColor: string;
  buttonBorderColor: string;

  // Border colors
  borderColor: string;
  borderColorLight: string;
  borderColorMedium: string;
  borderColorDark: string;

  // Icon colors
  iconColor: string;
  iconColorPrimary: string;
  iconColorSecondary: string;
  iconColorDisabled: string;

  // Shadow colors
  shadowColor: string;
  backdropColor: string;
}

export interface ThemingContext {
  isDark: Readonly<Ref<boolean>>;
  themeMode: Readonly<Ref<ThemeMode>>;
  colors: ComputedRef<ThemeColors>;
  toggleTheme: () => void; // Toggle entre modern et dark
  toggleStyleMode: () => void; // Toggle entre modern et classic (quand pas dark)
  setThemeMode: (mode: ThemeMode) => void;
  getCSSVariable: (variableName: string) => string;
  setCSSVariable: (variableName: string, value: string) => void;
  getContrastColor: (backgroundColor: string) => string;
  isHighContrast: () => boolean;
}

// CSS variable mapping
const CSS_VAR_MAP = {
  // Brand colors
  primary: '--ion-color-primary',
  secondary: '--ion-color-secondary',
  tertiary: '--ion-color-tertiary',

  // Semantic colors
  success: '--ion-color-success',
  warning: '--ion-color-warning',
  danger: '--ion-color-danger',
  info: '--ion-color-info',

  // Background colors
  background: '--ion-background-color',
  backgroundPrimary: '--ion-background-color-primary',
  backgroundSecondary: '--ion-background-color-secondary',
  backgroundTertiary: '--ion-background-color-tertiary',
  backgroundQuaternary: '--ion-background-color-quaternary',

  // Text colors
  text: '--ion-text-color',
  textPrimary: '--ion-text-color-primary',
  textSecondary: '--ion-text-color-secondary',
  textTertiary: '--ion-text-color-tertiary',
  textDisabled: '--ion-text-color-disabled',

  // Component colors
  toolbarBackground: '--ion-toolbar-background',
  toolbarColor: '--ion-toolbar-color',
  cardBackground: '--ion-card-background',
  cardColor: '--ion-card-color',
  itemBackground: '--ion-background-color',
  itemColor: '--ion-item-color',

  // Input colors
  inputBackground: '--ion-input-background',
  inputColor: '--ion-input-color',
  inputBorderColor: '--ion-input-border-color',
  inputBorderColorFocus: '--ion-input-border-color-focus',
  inputPlaceholderColor: '--ion-input-placeholder-color',

  // Button colors
  buttonBackground: '--ion-button-background',
  buttonColor: '--ion-button-color',
  buttonBorderColor: '--ion-button-border-color',

  // Border colors
  borderColor: '--ion-border-color',
  borderColorLight: '--ion-border-color-light',
  borderColorMedium: '--ion-border-color-medium',
  borderColorDark: '--ion-border-color-dark',

  // Icon colors
  iconColor: '--ion-icon-color',
  iconColorPrimary: '--ion-icon-color-primary',
  iconColorSecondary: '--ion-icon-color-secondary',
  iconColorDisabled: '--ion-icon-color-disabled',

  // Shadow colors
  shadowColor: '--ion-shadow-color',
  backdropColor: '--ion-backdrop-color',
} as const;

let themingInstance: ThemingContext | null = null;

export function useTheming(): ThemingContext {
  if (themingInstance) {
    return themingInstance;
  }

  const themeMode = ref<ThemeMode>('system');
  const isDark = ref(false);

  // Get CSS variable value
  const getCSSVariable = (variableName: string): string => {
    return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
  };

  // Set CSS variable value
  const setCSSVariable = (variableName: string, value: string): void => {
    document.documentElement.style.setProperty(variableName, value);
  };

  // Create reactive colors object
  const colors = computed<ThemeColors>(() => {
    const colorValues = {} as ThemeColors;

    for (const [key, cssVar] of Object.entries(CSS_VAR_MAP)) {
      (colorValues as any)[key] = getCSSVariable(cssVar);
    }

    return colorValues;
  });

  // Update theme based on mode
  const updateTheme = () => {
    const savedTheme = storageService.getItem('theme-mode') as ThemeMode;
    if (savedTheme) {
      themeMode.value = savedTheme;
    }

    const mode = themeMode.value;
    const body = document.body;

    // Déterminer si le mode sombre doit être actif
    const shouldBeDark =
      mode === 'dark' ||
      mode === 'classic' ||
      (mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    // Mettre à jour l'état dark
    isDark.value = shouldBeDark;
    body.classList.toggle('dark', shouldBeDark);

    // Appliquer la classe de thème spécifique
    body.classList.remove('dark-theme', 'classic-theme');
    if (mode === 'classic') {
      body.classList.add('classic-theme');
    } else if (shouldBeDark) {
      // dark explicite OU system dark → même palette custom
      body.classList.add('dark-theme');
    }
  };

  // Set theme mode
  const setThemeMode = (mode: ThemeMode) => {
    themeMode.value = mode;
    storageService.setItem('theme-mode', mode);
    updateTheme();
  };

  // Toggle between modern and dark (main dark mode toggle)
  const toggleTheme = () => {
    if (themeMode.value === 'dark') {
      setThemeMode('modern');
    } else {
      setThemeMode('dark');
    }
  };

  // Toggle between modern and classic (style mode toggle)
  const toggleStyleMode = () => {
    if (themeMode.value === 'classic') {
      setThemeMode('modern');
    } else if (themeMode.value === 'modern') {
      setThemeMode('classic');
    }
    // If currently dark, don't change (dark mode has priority)
  };

  // Get contrast color (black or white) for a given background
  const getContrastColor = (backgroundColor: string): string => {
    // Remove var() wrapper if present
    const color = backgroundColor.replace(/var\(([^)]+)\)/, (_, varName) =>
      getCSSVariable(varName)
    );

    // Simple luminance calculation
    const rgb = color.match(/\d+/g);
    if (!rgb || rgb.length < 3) return '#000000';

    const luminance =
      (parseInt(rgb[0]) * 0.299 + parseInt(rgb[1]) * 0.587 + parseInt(rgb[2]) * 0.114) / 255;

    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  // Check if high contrast mode is preferred
  const isHighContrast = (): boolean => {
    return window.matchMedia('(prefers-contrast: high)').matches;
  };

  // Initialize theme
  updateTheme();

  // Listen for system theme changes
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', () => {
    if (themeMode.value === 'system') {
      updateTheme();
    }
  });

  // Listen for high contrast changes
  const contrastQuery = window.matchMedia('(prefers-contrast: high)');
  contrastQuery.addEventListener('change', () => {
    // Trigger reactivity update if needed
    updateTheme();
  });

  // Watch for theme mode changes
  watch(themeMode, updateTheme);

  const instance: ThemingContext = {
    isDark: readonly(isDark),
    themeMode: readonly(themeMode),
    colors,
    toggleTheme,
    toggleStyleMode,
    setThemeMode,
    getCSSVariable,
    setCSSVariable,
    getContrastColor,
    isHighContrast,
  };

  themingInstance = instance;
  return instance;
}

// Utility functions for component usage
export const createThemedStyles = (
  stylesFn: (colors: ThemeColors, isDark: boolean, themeMode: ThemeMode) => Record<string, any>
) => {
  return computed(() => {
    const theming = useTheming();
    return stylesFn(theming.colors.value, theming.isDark.value, theming.themeMode.value);
  });
};

// Helper to create CSS custom properties object
export const createCSSProperties = (colors: Partial<ThemeColors>): Record<string, string> => {
  const cssProps: Record<string, string> = {};

  for (const [key, value] of Object.entries(colors)) {
    const cssVar = CSS_VAR_MAP[key as keyof typeof CSS_VAR_MAP];
    if (cssVar && value) {
      cssProps[cssVar] = value;
    }
  }

  return cssProps;
};

// Theme utility functions
export const isModernTheme = (): boolean => {
  const theming = useTheming();
  return theming.themeMode.value === 'modern';
};

export const isClassicTheme = (): boolean => {
  const theming = useTheming();
  return theming.themeMode.value === 'classic';
};

export const isDarkTheme = (): boolean => {
  const theming = useTheming();
  return theming.themeMode.value === 'dark';
};

// Helper to get theme-specific color variants
export const getThemeColors = () => {
  const theming = useTheming();
  const { colors, themeMode } = theming;

  return computed(() => ({
    primary: colors.value.primary,
    secondary: colors.value.secondary,
    accent: themeMode.value === 'classic' ? '#6c7ce0' : '#2c316f',
    brand: themeMode.value === 'dark' ? '#32c3cd' : '#091667',
  }));
};

export default useTheming;
